import { allSides } from '@/schemas/common';
import type { ProjectSnapshotData } from './ProjectSnapshot';
import { jsonBytes } from './PackedTiles';

type Circuit = ProjectSnapshotData['chips'][number]['logicCircuitDto'];
type Graph = NonNullable<Circuit['connectionGraphDto']>;
// Each endpoint combines the shared tile-string reference and two side bits.
// Null means no authored graph; [] and an absent disconnected list stay distinct.
export type PackedConnections = (null | [number[], number[]?])[];
const MAX_CONNECTIONS = 1_000_000;
const MAX_EXPANDED_BYTES = 64 * 1024 * 1024;

export function packConnections(project: ProjectSnapshotData, strings: string[]) {
  const references = new Map(strings.map((value, index) => [value, index]));
  const connections: PackedConnections = [];
  const costs = new Map<string, number>();
  let count = 0,
    expanded = 0,
    text = strings.reduce((sum, value) => sum + value.length, 0);
  const endpoint = (point: Graph['connections'][number]['a']): number => {
    const side = allSides.indexOf(point.side);
    if (side < 0) throw new TypeError('Invalid connection side');
    let index = references.get(point.tileId);
    if (index === undefined) {
      if ((text += point.tileId.length) > 64 * 1024 * 1024 || strings.length >= 4_000_000)
        throw new RangeError('Packed connection text exceeds limit');
      index = strings.length;
      references.set(point.tileId, index);
      strings.push(point.tileId);
    }
    let cost = costs.get(point.tileId);
    if (cost === undefined) {
      cost = jsonBytes(point.tileId) + 48;
      costs.set(point.tileId, cost);
    }
    expanded += cost;
    if (expanded > MAX_EXPANDED_BYTES)
      throw new RangeError('Packed connection expansion exceeds limit');
    return index * 4 + side;
  };
  const list = (values: Graph['connections']) => {
    const result: number[] = [];
    for (const value of values) {
      if (++count > MAX_CONNECTIONS)
        throw new RangeError('Packed connection count exceeds limit');
      result.push(endpoint(value.a), endpoint(value.b));
    }
    return result;
  };
  const circuit = (value: Circuit): Circuit => {
    const graph = value.connectionGraphDto;
    if (graph === undefined) connections.push(null);
    else {
      const packed: [number[], number[]?] = [list(graph.connections)];
      if (graph.disconnected !== undefined) packed.push(list(graph.disconnected));
      connections.push(packed);
    }
    const { connectionGraphDto: _, ...rest } = value;
    return rest;
  };
  return {
    project: {
      ...project,
      chips: project.chips.map((chip) => ({
        ...chip,
        logicCircuitDto: circuit(chip.logicCircuitDto),
        structureCircuitDto: circuit(chip.structureCircuitDto),
      })),
    },
    connections,
  };
}

/** Validate the complete expanded budget before allocating endpoint objects.
 * Caller has already validated the shared PackedTiles string table. */
export function unpackConnections(
  project: ProjectSnapshotData,
  strings: string[],
  input: unknown,
): ProjectSnapshotData {
  if (!Array.isArray(input) || input.length !== project.chips.length * 2)
    throw new TypeError('Invalid packed connection circuits');
  for (const chip of project.chips)
    for (const circuit of [chip.logicCircuitDto, chip.structureCircuitDto])
      if ('connectionGraphDto' in circuit)
        throw new TypeError('Unexpected unpacked connections in compact manifest');
  const costs = new Map<number, number>();
  let count = 0,
    expanded = 0;
  for (const graph of input) {
    if (graph === null) continue;
    if (!Array.isArray(graph) || (graph.length !== 1 && graph.length !== 2))
      throw new TypeError('Invalid packed connection graph');
    for (const list of graph) {
      if (!Array.isArray(list) || list.length % 2)
        throw new TypeError('Invalid packed connection list');
      count += list.length / 2;
      if (count > MAX_CONNECTIONS)
        throw new RangeError('Packed connection count exceeds limit');
      for (const endpoint of list) {
        if (
          !Number.isSafeInteger(endpoint) ||
          endpoint < 0 ||
          endpoint >= strings.length * 4
        )
          throw new TypeError('Invalid packed connection endpoint');
        const reference = Math.floor(endpoint / 4);
        let cost = costs.get(reference);
        if (cost === undefined) {
          cost = jsonBytes(strings[reference]) + 48;
          costs.set(reference, cost);
        }
        expanded += cost;
        if (expanded > MAX_EXPANDED_BYTES)
          throw new RangeError('Packed connection expansion exceeds limit');
      }
    }
  }
  let index = 0;
  const endpoint = (value: number): Graph['connections'][number]['a'] => ({
    tileId: strings[Math.floor(value / 4)],
    side: allSides[value % 4],
  });
  const list = (values: number[]): Graph['connections'] => {
    const result: Graph['connections'] = [];
    for (let i = 0; i < values.length; i += 2)
      result.push({ a: endpoint(values[i]), b: endpoint(values[i + 1]) });
    return result;
  };
  const circuit = (value: Circuit): Circuit => {
    const packed = input[index++];
    if (packed === null) return value;
    const graph: Graph = { connections: list(packed[0]) };
    if (packed.length === 2) graph.disconnected = list(packed[1]);
    return { ...value, connectionGraphDto: graph };
  };
  return {
    ...project,
    chips: project.chips.map((chip) => ({
      ...chip,
      logicCircuitDto: circuit(chip.logicCircuitDto),
      structureCircuitDto: circuit(chip.structureCircuitDto),
    })),
  };
}
